/**
 * API Configuration
 *
 * This file centralizes all API endpoints and URLs used throughout the application.
 * The API base URL can be configured via environment variables.
 */

// Get the API base URL from environment variable or use default
const getApiBaseUrl = (): string => {
  // In Vite, environment variables are accessed via import.meta.env
  // and must be prefixed with VITE_
  const envUrl = import.meta.env.VITE_API_BASE_URL;

  if (envUrl) {
    return envUrl;
  }

  // Default URLs based on environment
  if (import.meta.env.DEV) {
    // Development mode - use proxy or localhost
    return ''; // Empty string means use relative URLs (same origin)
  } else {
    // Production mode - use production URL
    return 'https://www.groupmychannels.com';
  }
};

// API Base URL
export const API_BASE_URL = getApiBaseUrl();

// Auth endpoints
export const AUTH_ENDPOINTS = {
  // Google/YouTube OAuth
  GOOGLE_LOGIN: `${API_BASE_URL}/api/auth/google/login`,
  GOOGLE_CALLBACK: `${API_BASE_URL}/api/auth/google/callback`,
  YOUTUBE_SYNC: `${API_BASE_URL}/api/auth/youtube/sync`,
  GOOGLE_DISCONNECT: `${API_BASE_URL}/api/auth/google/disconnect`,
} as const;

// User API endpoints
export const USER_ENDPOINTS = {
  // User data endpoints
  USER: `${API_BASE_URL}/api/user`,
  SUBSCRIPTIONS: `${API_BASE_URL}/api/user/subscriptions`,
  CHANNELS_RSS: `${API_BASE_URL}/api/user/channels/rss`,
} as const;

// Helper function to build API URLs with query parameters
export const buildApiUrl = (endpoint: string, params?: Record<string, string>): string => {
  const url = new URL(endpoint, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.append(key, value);
    });
  }
  return url.toString();
};

// Export a function to get the full auth URL for redirects
export const getAuthUrl = (): string => {
  return AUTH_ENDPOINTS.GOOGLE_LOGIN;
};
